import * as Sentry from "@sentry/react";
import { UseQueryOptions, useQuery } from "react-query";

import axios from "axios";

import { USER_COLUMN_RS_ENDPOINT } from "api/data";

import { EntityKind } from "models/entityKind";
import { PolygonFilter } from "models/filter";

export const useFieldGroupDataQueryKeys = {
  fieldGroupData: "fieldGroupData"
};

export type UseFieldGroupDataQueryKeyT = keyof typeof useFieldGroupDataQueryKeys;

export interface FieldGroupColumn {
  name: string;
  propertyName: string;
  data: Record<string, string>; // uwi -> value
  withinSubgroupOrder: number;
  pythonScript?: string;
}

export interface FieldGroupData {
  columns: FieldGroupColumn[];
  uwis: string[];
  formattedUwis: string[];
}

async function fetchFieldGroupData(
  fieldGroup: string,
  entityKind: EntityKind,
  isOrg: boolean,
  filterId: string,
  projectId?: string | null,
  polygonFilter?: PolygonFilter | null
): Promise<FieldGroupData> {
  const projectIdParam = projectId ? `&projectId=${projectId}` : "";
  const polygonParam = polygonFilter
    ? `&polygon=${encodeURIComponent(JSON.stringify(polygonFilter))}`
    : "";

  const response = await axios.get<FieldGroupData>(
    `${USER_COLUMN_RS_ENDPOINT}/field-group/${encodeURIComponent(
      fieldGroup
    )}?entityKind=${entityKind}&isOrg=${
      isOrg ? "true" : "false"
    }&filterId=${filterId}${projectIdParam}${polygonParam}`
  );

  return response.data;
}

export interface UseFieldGroupDataQueryParamsT {
  fieldGroup: string;
  entityKind: EntityKind;
  isOrg: boolean;
  filterId: string;
  polygonFilter?: PolygonFilter | null;
  projectId?: string | null;
  enabled?: boolean;
  onSuccess?: (data) => void;
  onError?: (error) => void;
  queryConfig?: Omit<
    UseQueryOptions<FieldGroupData, unknown>,
    "queryKey" | "queryFn" | "enabled" | "onSuccess" | "onError"
  >;
}

export interface UseFieldGroupDataQueryReturnT {
  data: FieldGroupData | undefined;
  error;
  isError: boolean;
  isIdle: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  refetch: () => void;
}

export function useFieldGroupDataQuery(
  params: UseFieldGroupDataQueryParamsT
): UseFieldGroupDataQueryReturnT {
  const {
    fieldGroup,
    entityKind,
    isOrg,
    filterId,
    polygonFilter,
    projectId,
    enabled = true,
    onError,
    onSuccess,
    queryConfig
  } = params;

  const queryInfo = useQuery({
    queryKey: [
      useFieldGroupDataQueryKeys.fieldGroupData,
      fieldGroup,
      entityKind,
      isOrg,
      filterId,
      projectId,
      polygonFilter
    ],
    queryFn: () =>
      fetchFieldGroupData(
        fieldGroup,
        entityKind,
        isOrg,
        filterId,
        projectId,
        polygonFilter
      ),
    onError: (error) => {
      onError && onError(error);
    },
    onSuccess: (data) => {
      onSuccess && onSuccess(data);
    },
    enabled: enabled && !!fieldGroup && !!filterId,
    ...queryConfig
  });

  return {
    data: queryInfo.data,
    error: queryInfo.error,
    isError: queryInfo.status === "error",
    isIdle: queryInfo.status === "idle",
    isLoading: queryInfo.status === "loading",
    isFetching: queryInfo.isFetching,
    isSuccess: queryInfo.status === "success",
    refetch: queryInfo.refetch
  };
}

/**
 * Export field group data to Excel file
 * Downloads an Excel file containing all data for the specified field group
 */
export async function exportFieldGroupData(
  fieldGroup: string,
  entityKind: EntityKind,
  isOrg: boolean,
  filterId: string,
  projectId?: string | null
): Promise<void> {
  try {
    const projectIdParam = projectId ? `&projectId=${projectId}` : "";

    const response = await axios.get(
      `${USER_COLUMN_RS_ENDPOINT}/field-group/${encodeURIComponent(
        fieldGroup
      )}/export?entityKind=${entityKind}&isOrg=${
        isOrg ? "true" : "false"
      }&filterId=${filterId}${projectIdParam}`,
      {
        responseType: "blob"
      }
    );

    // Create download link and trigger download
    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers["content-disposition"];
    let filename = `${fieldGroup}_export.xlsx`;
    if (contentDisposition) {
      const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
      if (matches?.[1]) {
        filename = matches[1];
      }
    }

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
}
